import React, { useEffect, useMemo, useState } from 'react'
import { useAddPaymentMethodMutation, useLazyGetIsSurchargeAppliedQuery } from 'api/payment-gateway/paymentGateway'
import { cloneDeep, get } from 'lodash'
import { METHOD_MODEL } from 'components/payments/settings/methodModel'
import RenderPaymentForm from 'components/payments/RenderPaymentForm'
import { handleError } from 'utils/error'
import { DEFAULT_AUTOPAY_TIMING, PAYMENT_METHODS } from 'settings/constants/payment'
import { toast } from 'react-toastify'
import T from 'T'
import { shallowEqual, useSelector } from 'react-redux'
import usePaymentProvider from 'hooks/usePaymentProvider'
import FortisElements from 'components/fortis/FortisElements'
import { useLazyGetBillingAddressQuery } from 'api/billing/billingAddress'
import { Box, Button } from '@mui/material'
import { HHAlert } from 'components/common/HHAlert'

const { ACH } = PAYMENT_METHODS

const AddPaymentMethod = () => {
  const [addPaymentMethod, { isLoading: isAddingPaymentMethod }] = useAddPaymentMethodMutation()
  const [newMethodFields, setNewMethodFields] = React.useState(cloneDeep(METHOD_MODEL))
  const [isSaved, setIsSaved] = useState(false)
  const accountId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.accountId'), shallowEqual)
  const businessId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.businessId'), shallowEqual)
  const { isFortis } = usePaymentProvider()

  const [getSurcharge, { data: surchargeData }] = useLazyGetIsSurchargeAppliedQuery()
  const surchargeApplied = useMemo(() => Boolean(surchargeData), [surchargeData])
  const [getBillingAddress, { data: billingAddressData }] = useLazyGetBillingAddressQuery()
  const billingAddress = useMemo(() => get(billingAddressData, 'billingAddress', {}), [billingAddressData])

  const { accountToken, expiry, cardHolderName, cardNickName, cvv, accountType, type, autoPay } = newMethodFields

  const handleChange = (key, value) => setNewMethodFields(method => ({ ...method, [key]: value }))

  const handleChangeBulk = value => setNewMethodFields(method => ({ ...method, ...value }))

  const resetFormFields = () => setNewMethodFields(cloneDeep(METHOD_MODEL))

  const handleClose = () => {
    resetFormFields()
  }

  const handleCardConnectSave = () => {
    const payload = {
      accountId,
      accountToken,
      expiry,
      cardHolderName,
      cardNickName,
      defaultAccount: false,
      // accounType if ACH possible values Saving or Checking , for Credit Card send this as null
      accountType: type === ACH ? accountType : null,
      autoPayTime: autoPay ? DEFAULT_AUTOPAY_TIMING : null,
      cvv,
    }
    addPaymentMethod({ businessId, payload })
      .unwrap()
      .then(response => {
        const status = get(response, 'status')
        if (status === 'Added') {
          toast.success(T.PAYMENT_METHOD_ADD_SUCCESS_MESSAGE)
          setIsSaved(true)
        } else if (status === 'Exists') {
          toast.error(T.PAYMENT_METHOD_ALREADY_EXISTS)
        } else {
          toast.error(status)
        }
        handleClose()
      })
      .catch(handleError)
  }

  const handleFortisSave = result => {
    const accountToken = get(result, 'data.id')
    const expiry = get(result, 'data.exp_date')
    const cardHolderName = get(result, 'data.account_holder_name')
    const cardNickName = get(result, 'data.account_holder_name')
    const accountType = get(result, 'data.account_type')
    const isCC = get(result, 'data.payment_method') === 'cc'
    const payload = {
      accountId,
      accountToken,
      expiry,
      cardHolderName,
      cardNickName,
      defaultAccount: false,
      // accounType if ACH possible values Saving or Checking , for Credit Card send this as null
      autoPayTime: autoPay ? DEFAULT_AUTOPAY_TIMING : null,
      ...(!isCC && { accountType: accountType === 'checking' ? 'Checking' : 'Saving' }),
    }

    addPaymentMethod({ businessId, payload })
      .unwrap()
      .then(response => {
        const status = get(response, 'status')
        if (status === 'Added') {
          toast.success(T.PAYMENT_METHOD_ADD_SUCCESS_MESSAGE)
          setIsSaved(true)
        } else if (status === 'Exists') {
          toast.error(T.PAYMENT_METHOD_ALREADY_EXISTS)
        } else {
          toast.error(status)
        }
        handleClose()
      })
      .catch(handleError)
  }

  useEffect(() => {
    if (businessId && accountId) {
      getSurcharge({ businessId })
      getBillingAddress({ businessId, accountId })
    }
  }, [businessId, accountId])

  if (isSaved) {
    return (
      <Box sx={{ py: 2 }}>
        <HHAlert severity="success" sx={{ mb: 2 }}>
          {T.PAYMENT_METHOD_ADD_SUCCESS_MESSAGE}
        </HHAlert>
        <Button fullWidth variant="outlined" onClick={() => setIsSaved(false)}>
          {T.ADD_ANOTHER_PAYMENT_METHOD}
        </Button>
      </Box>
    )
  }

  if (isFortis) {
    // Fortis path: render Elements Ticket flow; it will handle its own submit in the hosted form
    return (
      <FortisElements
        action="tokenization"
        onPaymentFinished={handleFortisSave}
        surchargeApplied={surchargeApplied}
        billingAddress={billingAddress}
      />
    )
  }

  return (
    <RenderPaymentForm
      isLoading={isAddingPaymentMethod}
      onChange={handleChange}
      onChangeBulk={handleChangeBulk}
      formFields={newMethodFields}
      onClose={handleClose}
      onSave={handleCardConnectSave}
    />
  )
}

AddPaymentMethod.propTypes = {}

export default AddPaymentMethod
